import { MapViewer } from '@3ddv/dvm-internal';
import { inject, Injectable } from '@angular/core';
import { DvmService } from '@features/digital-venue/services/dvm.service';
import { ConnectionService } from '@features/pages/select-seat/services/connection.service';
import { map, Observable, ReplaySubject, throwError } from 'rxjs';
import { ModalsService } from '../modals/modals.service';
import {
  GeneralAvailabilityDict,
  Seat,
  SeatAvailabilityDict,
  SeatAvailabilitySectionDict,
} from '../models';
import { PriceRange } from '../models/seat-availability.model';
import { ConfigurationService } from './configuration.service';
import { TutorialService } from './tutorial.service';

@Injectable({
  providedIn: 'root',
})
export class AvailabilityService {
  viewer: MapViewer;
  sectionsAvailability: GeneralAvailabilityDict = {}; // Coming from load handler.
  seatsAvailability: SeatAvailabilitySectionDict = {}; // Coming from load handler.
  sectionsOnlyAdas: GeneralAvailabilityDict = {};
  minSeatsLimit: number;
  maxSeatsLimit: number;
  adaSeats: Seat[] = [];
  priceRange$ = new ReplaySubject<PriceRange>(1);
  priceRange: PriceRange = { min: 100000000, max: -1 }; // Están seteados así para que cambien al compararlos con los valores de los tickets.
  public test: { min: number; max: number } = { min: 100000000, max: -1 };

  private dvmService = inject(DvmService);
  private connectionService = inject(ConnectionService);
  private configurationService = inject(ConfigurationService);
  private modalsService = inject(ModalsService);
  private tutorialService = inject(TutorialService);

  /**
   * Initializes the service and subscribes to the DVM viewer subject.
   * Handles errors during viewer initialization by showing a modal.
   */
  constructor() {
    const viewerSubscription = this.dvmService.viewerSubject.subscribe({
      next: response => (this.viewer = response),
      error: error => {
        console.error(error);
        const modalData = {
          title: 'ERROR',
          content: 'An Error occurred while trying to get the Viewer.',
          acceptBtnName: 'CLOSE',
        };
        this.modalsService.openModal(modalData);
      },
    });
    this.dvmService.viewerSubscriptions.push(viewerSubscription);
  }

  /**
   * Fetches the general availability for sections for a given event.
   * It filters out sections with no available seats (regular or ADA),
   * separates sections that *only* have ADA seats, calculates the overall price range,
   * and stores the availability data.
   * Handles tutorial mode by returning mock data.
   * @param eventId - The ID of the event to fetch availability for. Defaults to the current event ID from ConnectionService.
   * @returns An Observable emitting an array of available section IDs (MMC IDs).
   */
  getSectionAvailability(eventId = this.connectionService.eventId) {
    if (this.tutorialService.isOpen()) {
      return new Observable<Array<string>>(observer => {
        this.sectionsAvailability =
          this.tutorialService.getTutorialSectionAvailability();

        observer.next(this.tutorialService.getTutorialSectionAvailabilityIds());
        observer.complete();
      });
    }

    // Validate event ID before making the API call.
    if (!this.connectionService.eventId) {
      return throwError(() => ({
        error: {
          message: `The event requested is invalid. If you are being redirected from an external website, please call ${this.configurationService.configuration.phone} or email ${this.configurationService.configuration.email}.`,
          code: 'E_INVALID_EVENT_ID',
        },
      }));
    }

    return this.connectionService.getGeneralAvailability(eventId).pipe(
      map(({ data }) => {
        const result: string[] = [];
        const availability = data as GeneralAvailabilityDict;

        // Process each section's availability data.
        Object.entries(availability).forEach(([mmcId, item]) => {
          // Skip sections with zero total availability.
          if (item.availableQuantity + item.availableQuantityAda === 0) {
            delete availability[mmcId];
          } else {
            // Separate sections that only have ADA seats available.
            if (item.availableQuantity === 0 && item.availableQuantityAda > 0) {
              this.sectionsOnlyAdas[mmcId] = item;
              delete availability[mmcId];
              return;
            }
            if (item.priceRange[0].value < this.priceRange.min) {
              // Update the minimum price.
              this.priceRange.min = item.priceRange[0].value;
            }
            if (item.priceRange[1].value > this.priceRange.max) {
              // Update the max price.
              this.priceRange.max = item.priceRange[1].value;
            }
            this.priceRange$.next(this.priceRange);
            result.push(mmcId);
          }
        });

        // Store the filtered availability data (excluding ADA-only sections).
        this.sectionsAvailability = availability;

        return result;
      })
    );
  }

  /**
   * Fetches detailed seat availability for a given list of sections and event.
   * It retrieves data for all specified sections and then organizes it into the
   * `seatsAvailability` dictionary, keyed by section ID.
   * Handles tutorial mode by returning mock data.
   * @param sectionsArray - An array of section IDs (MMC IDs) to fetch seat availability for.
   * @param eventId - The ID of the event. Defaults to the current event ID from ConnectionService.
   * @returns An Observable emitting an array of all available seat IDs across the requested sections.
   */
  getSeatAvailability(
    sectionsArray: string[],
    eventId = this.connectionService.eventId
  ) {
    if (this.tutorialService.isOpen()) {
      return new Observable<Array<string>>(observer => {
        this.seatsAvailability =
          this.tutorialService.getTutorialSeatsAvailability();

        observer.next(this.tutorialService.getTutorialSeatsAvailabilityIds());
        observer.complete();
      });
    }

    return this.connectionService
      .getMultiSectionAvailability(eventId, sectionsArray)
      .pipe(
        map(({ data }) => {
          // The API returns a flat dictionary of all seats; we need to group them by section.
          const availability = data as SeatAvailabilityDict;

          sectionsArray.forEach(sectionId => {
            // Filter availability for this specific section
            const filteredAvailability = Object.entries(
              availability as SeatAvailabilityDict
            ).filter(([seatId, _]) => seatId.split('-')[0] === sectionId);
            this.seatsAvailability[sectionId] = Object.fromEntries(
              // Store the filtered seats for the current section.
              filteredAvailability
            ) as SeatAvailabilityDict;
          });

          return Object.keys(availability as SeatAvailabilityDict);
        })
      );
  }

  /**
   * Sets the visual state of sections that *only* have ADA seats to 'available' on the viewer.
   */
  activeAdaSections() {
    this.dvmService.viewer.setAvailable(
      'section',
      Object.keys(this.sectionsOnlyAdas)
    );
  }

  /**
   * Sets the visual state of sections that *only* have ADA seats to 'unavailable' on the viewer.
   */
  deactivateOnlyAdaSections() {
    this.dvmService.viewer.setUnavailable(
      'section',
      Object.keys(this.sectionsOnlyAdas)
    );
  }

  /**
   * Retrieves the detailed availability information for a specific seat ID from the cached data.
   * Assumes `getSeatAvailability` has already been called for the relevant section.
   * @param seatId - The full seat ID (e.g., 'SECTION-ROW-SEAT').
   * @returns The SeatAvailability object for the given seat, or undefined if not found.
   */
  getSeatByIdFromAvailability(seatId: string) {
    // Extracts the section ID from the full seat ID to look up in the correct dictionary entry.
    return this.seatsAvailability[seatId.split('-')[0]][seatId];
  }
}
